import { useCallback, useState } from "react";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { downloadAndSaveSupportBundle } from "@/utils/support-bundle";
import { useCreateEnvironmentLogsToken, type PlainEnvironment } from "@/queries/environment-queries";

export const useEnvironmentSupportBundle = (environment: PlainEnvironment) => {
    const supportBundleUrl = environment.status?.environmentUrls?.supportBundle;

    // Defer the token fetch until the user signals intent (e.g. opens the actions menu).
    // Previously this fired eagerly for every environment on sidebar load.
    const [tokenEnabled, setTokenEnabled] = useState(false);
    const { data: logsToken } = useCreateEnvironmentLogsToken(tokenEnabled ? environment.id : undefined);

    const isSupportBundleAvailable = !!supportBundleUrl;

    const enableTokenFetch = useCallback(() => {
        if (supportBundleUrl) {
            setTokenEnabled(true);
        }
    }, [supportBundleUrl]);

    const downloadSupportBundle = useCallback(async () => {
        if (!supportBundleUrl || !logsToken) {
            return;
        }
        const generatingToast = toast({ title: "Generating support bundle...", indefinite: true });
        try {
            await downloadAndSaveSupportBundle({
                supportBundleUrl,
                logsToken,
                filename: `environment-support-bundle-${environment.id}.zip`,
            });
            generatingToast.dismiss();
            toast({ title: "Support bundle downloaded" });
        } catch (error) {
            generatingToast.dismiss();
            toast({
                title: "Failed to download support bundle",
                description: formatError(error),
            });
        }
    }, [supportBundleUrl, logsToken, environment.id]);

    return {
        downloadSupportBundle,
        isSupportBundleAvailable,
        enableTokenFetch,
        isTokenReady: !!logsToken,
    };
};
